import { useSuspenseQuery } from "@tanstack/react-query";
import { Button, buttonVariants } from "@workspace/web-ui/components/button";
import { cn } from "@workspace/web-ui/lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@workspace/web-ui/components/dialog";

import paymentError from "~/assets/images/consultation-payment-error.svg";
import paymentSuccess from "~/assets/images/consultation-payment-success.svg";
import { getPrescriptionMedicationQueryOptions } from "../../api/queries";


export const MedicationPaymentSuccessModal = ({
    open,
    onClose,
    prescriptionId,
    program,
}: {
    open: boolean;
    onClose: () => void;
    prescriptionId: string;
    program: string;
}) => {
    const { data } = useSuspenseQuery(getPrescriptionMedicationQueryOptions(prescriptionId ?? ""))
    const medicationResponse = data?.prescription.medications;

    if (!medicationResponse) {
        return null;
    }

    const totalCost = medicationResponse?.reduce((sum, item) => sum + item.cost, 0) || 0;
    const medications = medicationResponse?.map((item) => item.name).join(", ");

    const handleClose = () => {
        window.history.replaceState({}, "", window.location.pathname);
        onClose?.();
    }

    return (
        <Dialog open={open} onOpenChange={handleClose} >
            <div
                className={` ${open ? "fixed inset-0 z-40 h-screen bg-black/5 backdrop-blur-sm" : ""} `}
            />
            <DialogContent className="max-w-sm border-0 rounded-lg sm:max-w-md p-0">
                {/* hide the header with sr only */}
                <DialogHeader className="sr-only">
                    <DialogTitle className="sr-only">
                        Payment success
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-col items-center text-center">
                    <div className="p-4 md:p-6 flex flex-col items-center text-center">
                        <div className="size-20 md:size-24 mb-2 md:mb-3">
                            <img src={paymentSuccess} alt="payment-image" className="w-full" />
                        </div>
                        <h2 className="text-2xl md:text-[32px] font-semibold">Payment Successful</h2>
                        <p className="text-xs md:text-sm flex text-center w-full text-muted-foreground mt-1 px-4 md:px-6">
                            Your order will be processed and shipped shortly. You'll receive updates on your medication status via your Medical Page and email updates.
                        </p>
                    </div>

                    <div className="w-full flex flex-col mt-4 md:mt-6 text-xs md:text-sm text-left space-y-3 md:space-y-4 px-4 md:px-6">
                        <div className="flex justify-between">
                            <span className="#2D2F35">Medication</span>
                            <span className="text-[#202020CC] font-medium">{medications}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="#2D2F35">Amount:</span>
                            <span className="text-[#202020CC] font-medium">${totalCost}.00</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="#2D2F35">Program:</span>
                            <span className="text-[#202020CC] font-medium">{program}</span>
                        </div>
                    </div>

                    <div className="flex justify-center w-full px-4 md:px-6 mt-4 md:mt-6 py-4 md:py-6 border-t border-[#E0E0E0]">

                        <Button onClick={handleClose} className={cn(buttonVariants({ variant: "default" }), "w-full rounded-lg bg-[#4272DD]")}>Track medication status</Button>
                    </div>

                </div>
            </DialogContent>
        </Dialog>
    );
};

export const MedicationPaymentErrorModal = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}) => {
    const handleClose = () => {
        window.history.replaceState({}, "", window.location.pathname);
        onClose?.();
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <div
                className={` ${open ? "fixed inset-0 z-40 h-screen bg-black/5 backdrop-blur-sm" : ""} `}
            />
            <DialogContent className="max-w-sm border-0 rounded-lg sm:max-w-sm p-0">
                {/* hide the header with sr only */}
                <DialogHeader className="sr-only">
                    <DialogTitle className="sr-only">
                        Payment error
                    </DialogTitle>
                </DialogHeader>

                <div className="flex flex-col items-center text-center p-4 md:p-6">
                    <div className="size-20 md:size-24 mb-2 md:mb-3">
                        <img src={paymentError} alt="payment-image" className="w-full" />
                    </div>
                    <h2 className="text-2xl md:text-[32px] font-semibold">Payment Failed!</h2>
                    <p className="text-xs md:text-sm flex w-full md:w-[80%] text-center text-muted-foreground mt-1">
                        We couldn&apos;t process your payment. This could be due to insufficient funds, incorrect card details, or a temporary issue with the payment processor.
                    </p>

                    <Button onClick={handleClose} className={cn(buttonVariants({ variant: "default" }), "mt-4 md:mt-6 w-full rounded-lg bg-[#4272DD]")}>Go to dashboard</Button>

                </div>
            </DialogContent>
        </Dialog>
    );
};
